import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useFetchAgentsList } from 'hooks/api/useTicketAgentsList';
import { useReassignTickets } from 'hooks/api/useTickets';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import type { ReassignTicketFormValuesT } from '../declarations';

type UseReassignTicketPopupT = {
  isLoadingAgentOptions: boolean;
  agentIdsOptions: SelectOptionT[];
  isPopupOpened: boolean;
  handleSubmitForm: (data: ReassignTicketFormValuesT, onClose: NoopT | undefined) => void;
  isReassigningTicket: boolean;
  handleCloseReassignTicketPopup: (onClosePopupCb: NoopT | undefined) => NoopT;
  handleAgentChange: (roofTopId: string) => void;
};

const useReassignTicketPopup = (): UseReassignTicketPopupT => {
  /**
   * Hooks
   */
  const {
    setReassignTicketPopupVisibility,
    isReassignTicketPopupOpened,
    selectedTicketsIds,
    setSelectedTicketsIds,
    selectedTicketsRooftopIds,
    selectedTicketId,
  } = useTickets();
  const [, setSelectedAgentId] = useState<string | undefined>(undefined);
  const { isLoadingUserProfile } = useUserProfile();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  /**
   * Api hooks
   */
  const reassignTicketMutation = useReassignTickets({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
  });

  const ticketUseFetchAgentsListResult = useFetchAgentsList(
    isReassignTicketPopupOpened,
    selectedTicketsRooftopIds
  );

  /**
   * Variables
   */
  const agentIdsOptions = useMemo(
    () =>
      ticketUseFetchAgentsListResult.data
        ? ticketUseFetchAgentsListResult.data
            .map(({ userId, displayName }) => ({ label: displayName, value: userId }))
            .sort((a, b) => {
              if (a.label > b.label) {
                return 1;
              }

              if (a.label < b.label) {
                return -1;
              }

              return 0;
            })
        : [],
    [ticketUseFetchAgentsListResult.data]
  );

  /**
   * Handlers
   */
  const handleCloseReassignTicketPopup = useCallback(
    (onClosePopupCb: NoopT | undefined) => () => {
      onClosePopupCb ? onClosePopupCb() : setReassignTicketPopupVisibility(false);
      setSelectedTicketsIds([]);
    },
    [setReassignTicketPopupVisibility, setSelectedTicketsIds]
  );

  const handleSubmitForm = useCallback(
    (data: ReassignTicketFormValuesT, onClose: NoopT | undefined) => {
      reassignTicketMutation.mutate(
        {
          ...data,
          ticketIds: selectedTicketId ? [selectedTicketId] : selectedTicketsIds,
        },
        {
          onSuccess: () => {
            dispatch(
              updateSnackbarNotification({
                snackbarMessage: 'Ticket successfully reassigned',
                snackBarSeverity: 'success',
              })
            );
            onClose ? onClose() : setReassignTicketPopupVisibility(false);
            setSelectedTicketsIds([]);
          },
          onError: error => {
            const errorMsg =
              getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
            dispatch(
              updateSnackbarNotification({
                snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
                snackBarSeverity: 'error',
              })
            );
            onClose ? onClose() : setReassignTicketPopupVisibility(false);
            setSelectedTicketsIds([]);
          },
        }
      );
    },
    [
      reassignTicketMutation,
      selectedTicketsIds,
      selectedTicketId,
      dispatch,
      setReassignTicketPopupVisibility,
      setSelectedTicketsIds,
    ]
  );

  const handleAgentChange = (agentId: string): void => {
    setSelectedAgentId(agentId);
  };

  return {
    isLoadingAgentOptions: isLoadingUserProfile,
    agentIdsOptions,
    handleCloseReassignTicketPopup,
    isPopupOpened: isReassignTicketPopupOpened,
    handleSubmitForm,
    isReassigningTicket: reassignTicketMutation.isLoading,
    handleAgentChange,
  };
};

export default useReassignTicketPopup;
